import React, { useState, useEffect, useCallback } from "react";
import { View, StyleSheet } from "react-native";
import { useLocalSearchParams, useRouter, useNavigation } from "expo-router";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";
import { useFirebase } from "@/components/firebase/useFirebase";
import { useAuth } from "@/components/auth/useAuth";
import { getMyShares, share, unshare } from "@/services/firebase/share-functions";
import { ShareModel } from "common247";
import { ThemedKebabMenu, type IKebabActionItem } from "@/components/ThemedKebabMenu";
import { ShareEditor } from "@/components/sharing/ShareEditor";
import { navigateBackOrToList } from "@/utils/navigation";
import { useAppDispatch, useAppSelector } from "@/services/store/hooks";
import { addOrUpdateShare, removeShare, setShares } from "@/services/store/features/sharesSlice";
import { updateContactShare, removeContactShare } from "@/services/store/features/contactsSlice";
import { entitySync } from "@/services/sync/entity-sync";

/**
 * Screen for viewing and editing a single Share record in isolation.
 */
export default function ShareDetailsScreen(): React.JSX.Element {
  const { id } = useLocalSearchParams<{ id: string }>();
  const services = useFirebase();
  const { user } = useAuth();
  const router = useRouter();
  const navigation = useNavigation();
  const dispatch = useAppDispatch();

  const { shares } = useAppSelector((state) => state.shares);

  const [currentShare, setCurrentShare] = useState<ShareModel | null>(() => {
    return shares.find((s) => s.id === id) || null;
  });
  const [loading, setLoading] = useState(!currentShare);
  const [isSaving, setIsSaving] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  const fetchShareData = useCallback(async () => {
    if (!services || !id) return;
    try {
      const res = await getMyShares(services, {});
      if (res.status && res.shares) {
        dispatch(setShares(res.shares));
        const found = res.shares.find((s) => s.id === id);
        if (found) {
          setCurrentShare(found);
        } else {
          CustomAlert.alert("Error", "Share not found.");
          navigateBackOrToList(router, "/(tabs)/my-shares", navigation);
        }
      }
    } catch (error) {
      console.error("Error fetching share detail:", error);
      CustomAlert.alert("Error", "Failed to load share details.");
    } finally {
      setLoading(false);
    }
  }, [services, id, dispatch, router, navigation]);

  useEffect(() => {
    if (!currentShare) {
      fetchShareData();
    }
  }, [currentShare, fetchShareData]);

  const handleRoleChange = async (newRole: "viewer" | "editor") => {
    if (!services || !currentShare) return;
    setIsSaving(true);
    try {
      const res = await share(services, {
        sourceId: currentShare.sourceId,
        sourceType: currentShare.sourceType,
        targetId: currentShare.targetId,
        targetType: currentShare.targetType,
        role: newRole,
      });

      if (res.status) {
        const updatedShare: ShareModel = { ...currentShare, role: newRole };
        setCurrentShare(updatedShare);
        dispatch(addOrUpdateShare(updatedShare));
        if (currentShare.sourceType === "contact") {
          dispatch(updateContactShare({ contactId: currentShare.sourceId, share: updatedShare }));
        }
        entitySync.emit({
          type: "share",
          action: "update",
          id: currentShare.id || currentShare.sourceId,
          data: updatedShare,
        });
      } else {
        CustomAlert.alert("Error", res.error || "Failed to update share role");
      }
    } catch (error) {
      console.error("Error updating share role:", error);
      CustomAlert.alert("Error", "Unexpected error updating share role");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevoke = async () => {
    if (!services || !currentShare || !currentShare.id) return;
    setIsRevoking(true);
    try {
      const res = await unshare(services, { shareId: currentShare.id });
      if (res.status) {
        dispatch(removeShare(currentShare.id));
        if (currentShare.sourceType === "contact") {
          dispatch(removeContactShare({ contactId: currentShare.sourceId, shareId: currentShare.id }));
        } else {
          dispatch(removeContactShare({ shareId: currentShare.id }));
        }
        entitySync.emit({
          type: "share",
          action: "delete",
          id: currentShare.id,
          data: currentShare,
        });
        CustomAlert.alert("Access Revoked", "Share access was successfully revoked.");
        navigateBackOrToList(router, "/(tabs)/my-shares", navigation);
      } else {
        CustomAlert.alert("Error", res.error || "Failed to revoke share access");
      }
    } catch (error) {
      console.error("Error revoking share:", error);
      CustomAlert.alert("Error", "Unexpected error revoking share");
    } finally {
      setIsRevoking(false);
    }
  };

  const handleNavigateToSource = () => {
    if (!currentShare) return;
    switch (currentShare.sourceType) {
      case "memory":
        router.push(`/(tabs)/my-memories/${currentShare.sourceId}`);
        break;
      case "media":
        router.push(`/(tabs)/my-media/${currentShare.sourceId}`);
        break;
      case "contact":
        router.push(`/(tabs)/my-contacts/${currentShare.sourceId}`);
        break;
      case "group":
        router.push(`/(tabs)/my-groups/${currentShare.sourceId}`);
        break;
      default:
        break;
    }
  };

  const handleNavigateToTarget = () => {
    if (!currentShare) return;
    switch (currentShare.targetType) {
      case "contact":
        router.push(`/(tabs)/my-contacts/${currentShare.targetId}`);
        break;
      case "group":
        router.push(`/(tabs)/my-groups/${currentShare.targetId}`);
        break;
      default:
        break;
    }
  };

  const canEdit = true;
  const pageTitle = currentShare
    ? `${canEdit ? "Edit" : "View"}: ${currentShare.targetDisplayName || "Share"}`
    : "Share Details";

  const kebabActions: IKebabActionItem[] = [];
  if (currentShare) {
    const sourceTypeName =
      currentShare.sourceType.charAt(0).toUpperCase() + currentShare.sourceType.slice(1);
    const targetTypeName =
      currentShare.targetType.charAt(0).toUpperCase() + currentShare.targetType.slice(1);

    kebabActions.push({
      id: "openSource",
      label: `Open ${sourceTypeName}`,
      icon: "open-outline",
      onPress: handleNavigateToSource,
    });

    kebabActions.push({
      id: "viewTarget",
      label: `View ${targetTypeName}`,
      icon: "person-outline",
      onPress: handleNavigateToTarget,
    });

    kebabActions.push({
      id: "revoke",
      label: "Revoke Access",
      icon: "trash-outline",
      destructive: true,
      disabled: isRevoking,
      onPress: handleRevoke,
    });
  }

  return (
    <ThemedScreen
      title={pageTitle}
      leftIcon={{
        name: "arrow-back",
        onPress: () => navigateBackOrToList(router, "/(tabs)/my-shares", navigation),
      }}
      loading={loading}
      scrollable={false}
      rightComponent={
        kebabActions.length > 0 ? (
          <ThemedKebabMenu actions={kebabActions} accessibilityLabel="Share options" />
        ) : undefined
      }
    >
      <View style={styles.container}>
        <ThemedProgressBar loading={loading || isRevoking} />
        {!loading && currentShare && (
          <ShareEditor
            share={currentShare}
            canEdit={true}
            canRevoke={true}
            isSaving={isSaving}
            isRevoking={isRevoking}
            onRoleChange={handleRoleChange}
            onRevoke={handleRevoke}
            onNavigateToSource={handleNavigateToSource}
            onNavigateToTarget={handleNavigateToTarget}
            onCancel={() => navigateBackOrToList(router, "/(tabs)/my-shares", navigation)}
          />
        )}
      </View>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
